import { z } from 'zod';
import { slugSchema } from './catalog.js';

/**
 * Topik internal: pokok yang perlu penyesuaian, data, atau keputusan dari tim
 * CI (menu Pengaturan → Topik Internal). Hanya untuk panel admin.
 */

export const INTERNAL_TOPIC_STATUSES = [
  'OPEN',
  'WAITING_CI',
  'IN_PROGRESS',
  'COMPLETED',
  'CANCELLED',
] as const;
export const internalTopicStatusSchema = z.enum(INTERNAL_TOPIC_STATUSES);
export type InternalTopicStatus = z.infer<typeof internalTopicStatusSchema>;

export const INTERNAL_TOPIC_STATUS_LABEL: Record<InternalTopicStatus, string> = {
  OPEN: 'Open',
  WAITING_CI: 'Menunggu CI',
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
  CANCELLED: 'Dibatalkan',
};

/** Status yang masih perlu ditindaklanjuti. */
export const ACTIVE_INTERNAL_TOPIC_STATUSES: readonly InternalTopicStatus[] = [
  'OPEN',
  'WAITING_CI',
  'IN_PROGRESS',
];

export const INTERNAL_TOPIC_PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'] as const;
export const internalTopicPrioritySchema = z.enum(INTERNAL_TOPIC_PRIORITIES);
export type InternalTopicPriority = z.infer<typeof internalTopicPrioritySchema>;

export const INTERNAL_TOPIC_PRIORITY_LABEL: Record<InternalTopicPriority, string> = {
  LOW: 'Rendah',
  MEDIUM: 'Sedang',
  HIGH: 'Tinggi',
  URGENT: 'Mendesak',
};

export const INTERNAL_TOPIC_DETAIL_MAX = 8000;
export const INTERNAL_TOPIC_LINKS_MAX = 10;

export const internalTopicIdParamsSchema = z.object({
  id: z.string().min(1).max(64),
});
export type InternalTopicIdParams = z.infer<typeof internalTopicIdParamsSchema>;

/** Halaman di panel/situs (`/produk/kartu-nama`) atau tautan https. */
export const internalTopicLinkSchema = z.object({
  label: z.string().trim().min(1).max(80),
  url: z
    .string()
    .trim()
    .max(500)
    .refine(
      (url) => (url.startsWith('/') && !url.startsWith('//')) || /^https:\/\/[^\s]+$/.test(url),
      'Tautan harus diawali / atau https://',
    ),
});
export type InternalTopicLink = z.infer<typeof internalTopicLinkSchema>;

const dateOnlySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Format tanggal YYYY-MM-DD')
  .refine((value) => !Number.isNaN(Date.parse(`${value}T00:00:00Z`)), 'Tanggal tidak valid');

// ------------------------------------------------------------
// Kategori
// ------------------------------------------------------------

/** Field mentah tanpa default; .partial() tidak membatalkan .default() (lihat schemas/article.ts). */
const categoryFields = {
  slug: slugSchema,
  name: z.string().trim().min(2).max(80),
  description: z.string().trim().max(300).nullable(),
  sortOrder: z.number().int().min(0).max(10_000),
  isActive: z.boolean(),
};

export const internalTopicCategoryInputSchema = z.object({
  ...categoryFields,
  description: categoryFields.description.default(null),
  sortOrder: categoryFields.sortOrder.default(0),
  isActive: categoryFields.isActive.default(true),
});
export type InternalTopicCategoryInput = z.infer<typeof internalTopicCategoryInputSchema>;

export const internalTopicCategoryUpdateSchema = z
  .object(categoryFields)
  .partial()
  .refine((value) => Object.keys(value).length > 0, 'Tidak ada perubahan yang dikirim');
export type InternalTopicCategoryUpdate = z.infer<typeof internalTopicCategoryUpdateSchema>;

// ------------------------------------------------------------
// Topik
// ------------------------------------------------------------

const topicFields = {
  categoryId: z.string().min(1).max(64),
  title: z.string().trim().min(3).max(200),
  detail: z.string().trim().max(INTERNAL_TOPIC_DETAIL_MAX),
  status: internalTopicStatusSchema,
  priority: internalTopicPrioritySchema,
  pic: z.string().trim().max(120).nullable(),
  dueDate: dateOnlySchema.nullable(),
  links: z.array(internalTopicLinkSchema).max(INTERNAL_TOPIC_LINKS_MAX),
};

export const internalTopicInputSchema = z.object({
  ...topicFields,
  detail: topicFields.detail.default(''),
  status: topicFields.status.default('OPEN'),
  priority: topicFields.priority.default('MEDIUM'),
  pic: topicFields.pic.default(null),
  dueDate: topicFields.dueDate.default(null),
  links: topicFields.links.default([]),
});
export type InternalTopicInput = z.infer<typeof internalTopicInputSchema>;

export const internalTopicUpdateSchema = z
  .object(topicFields)
  .partial()
  .refine((value) => Object.keys(value).length > 0, 'Tidak ada perubahan yang dikirim');
export type InternalTopicUpdate = z.infer<typeof internalTopicUpdateSchema>;

export const internalTopicQuerySchema = z.object({
  categoryId: z.string().min(1).max(64).optional(),
  /** `active` = Open, Menunggu CI, dan In progress. */
  status: z.union([internalTopicStatusSchema, z.literal('active')]).optional(),
  priority: internalTopicPrioritySchema.optional(),
  q: z.string().trim().max(120).optional(),
});
export type InternalTopicQuery = z.infer<typeof internalTopicQuerySchema>;

// ------------------------------------------------------------
// Bentuk respons
// ------------------------------------------------------------

export interface InternalTopicCategoryDto {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  topicCount: number;
  /** Topik yang masih aktif (Open, Menunggu CI, In progress). */
  activeCount: number;
}

export interface InternalTopicDto {
  id: string;
  categoryId: string;
  categoryName: string;
  title: string;
  detail: string;
  status: InternalTopicStatus;
  priority: InternalTopicPriority;
  pic: string | null;
  /** YYYY-MM-DD. */
  dueDate: string | null;
  links: InternalTopicLink[];
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
}

export interface InternalTopicListDto {
  items: InternalTopicDto[];
  /** Jumlah per status dengan filter kategori, prioritas, dan pencarian yang sama (tanpa filter status). */
  counts: Record<InternalTopicStatus, number>;
}
